import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ScreenLoader } from '@/components/global/Loader';
import { useTheme } from '@/components/providers/ThemeProvider';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabaseClient } from '@/lib/supabase';
import { sendErrorToSentry } from '@/lib/utils';

import { ResetPasswordForm } from '../reset-password/-components/ResetPassword';

export const Route = createFileRoute('/(auth)/reset-password-on-invitation/')({
  component: RouteComponent,
});

function RouteComponent() {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { toast } = useToast();
  const { resolvedTheme } = useTheme();
  const [userEmail, setUserEmail] = useState<string>();

  const urlHash = useMemo(
    () => new URLSearchParams(window.location.hash.slice(1)),
    [],
  );

  useEffect(() => {
    (async () => {
      const refreshToken = urlHash.get('refresh_token');

      if (refreshToken === null) {
        return;
      }

      const {
        data: { user },
        error,
      } = await supabaseClient.auth.refreshSession({
        refresh_token: refreshToken,
      });

      if (user === null) {
        toast({
          title: 'auth.resetPasswordOnInvitation.error',
          variant: 'destructive',
        });

        if (error !== null) {
          // We do not throw bc it would crash the app
          sendErrorToSentry(error);
        }

        return;
      }

      setUserEmail(user.email);
    })();
  }, [toast, urlHash]);

  const hasExpiredToken =
    urlHash.get('error') === 'access_denied' &&
    urlHash.get('error_code') === 'otp_expired';

  if (hasExpiredToken) {
    const logoSrc =
      resolvedTheme === 'dark' ? '/LogoWhite.svg' : '/LogoBlack.svg';

    return (
      <section className="bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
          <Link
            to="/"
            className="flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white"
          >
            <img
              className="mr-2"
              src={logoSrc}
              alt="logo"
              width={75}
              height={60}
            />
          </Link>
          <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700">
            <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
              <h1 className="text-xl font-semibold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
                <FormattedMessage id="auth.resetPasswordOnInvitation.expired.title" />
              </h1>
              <p className="text-sm font-light text-gray-500 dark:text-gray-400">
                <FormattedMessage id="auth.resetPasswordOnInvitation.expired.description" />
              </p>
              <div className="flex flex-col space-y-3">
                <Link to="/forgot-password">
                  <Button className="w-full text-white bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800">
                    <FormattedMessage id="auth.resetPasswordOnInvitation.expired.resetPassword" />
                  </Button>
                </Link>
                <Link to="/sign-in">
                  <Button variant="outline" className="w-full">
                    <FormattedMessage id="auth.resetPasswordOnInvitation.expired.signIn" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (userEmail === undefined) {
    return <ScreenLoader />;
  }

  return (
    <ResetPasswordForm
      onPasswordReset={async password => {
        const { error } = await supabaseClient.auth.signInWithPassword({
          email: userEmail,
          password,
        });

        if (error !== null) {
          toast({
            title: 'auth.signIn.form.submit.error',
            variant: 'destructive',
          });

          throw error;
        }

        router.navigate({
          to: '/',
        });
      }}
      email={userEmail}
      title={formatMessage({
        id: 'auth.resetPasswordOnInvitation.title',
      })}
      subtitle={formatMessage({
        id: 'auth.resetPasswordOnInvitation.subtitle',
      })}
    />
  );
}
